/**
 * Use Issue Interaction Hook
 *
 * Manages hover/touch state for issue highlights.
 * Handles the 200ms hover delay for popovers (web) and
 * touch activation for bottom sheets (native).
 *
 * Hover behavior (matching prototype):
 * - 200ms delay before showing popover on hover
 * - 300ms persist delay when mouse leaves highlight
 * - Popover stays open while mouse is over it
 *
 * DD-161: Added haptic feedback on native issue selection
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Platform } from 'react-native';
import * as Haptics from 'expo-haptics';

interface AnchorPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseIssueInteractionOptions {
  /** Hover delay in ms before showing popover (web only) */
  hoverDelay?: number;
  /** Persist delay in ms after mouse leaves before closing (web only) */
  hoverPersistDelay?: number;
  /** Whether interaction is enabled */
  enabled?: boolean;
}

interface UseIssueInteractionReturn {
  /** Currently active issue ID (or null) */
  activeIssueId: number | null;
  /** Anchor position for popover positioning */
  anchorPosition: AnchorPosition | null;
  /** Activate an issue (hover start or touch) */
  activateIssue: (issueId: number, position: AnchorPosition) => void;
  /** Deactivate the current issue */
  deactivateIssue: () => void;
  /** Handle mouse enter on highlight (web) */
  handleMouseEnter: (issueId: number, position: AnchorPosition) => void;
  /** Handle mouse leave on highlight (web) */
  handleMouseLeave: () => void;
  /** Handle touch/press - toggle behavior */
  handlePress: (issueId: number, position: AnchorPosition) => void;
  /** Set popover hovered state (call from popover mouse events) */
  setPopoverHovered: (hovered: boolean) => void;
}

const DEFAULT_HOVER_DELAY = 200;
const DEFAULT_HOVER_PERSIST_DELAY = 300;

/**
 * Hook for managing issue highlight interactions
 */
export function useIssueInteraction(
  options: UseIssueInteractionOptions = {}
): UseIssueInteractionReturn {
  const {
    hoverDelay = DEFAULT_HOVER_DELAY,
    hoverPersistDelay = DEFAULT_HOVER_PERSIST_DELAY,
    enabled = true,
  } = options;

  const [activeIssueId, setActiveIssueId] = useState<number | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | null>(null);

  // Track hover states
  const isPopoverHoveredRef = useRef(false);
  const isHighlightHoveredRef = useRef(false);

  // Timer refs
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Clear all timers helper
  const clearTimers = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const activateIssue = useCallback(
    (issueId: number, position: AnchorPosition) => {
      if (!enabled) return;
      clearTimers();
      setActiveIssueId(issueId);
      setAnchorPosition(position);
    },
    [enabled, clearTimers]
  );

  const deactivateIssue = useCallback(() => {
    clearTimers();
    isHighlightHoveredRef.current = false;
    isPopoverHoveredRef.current = false;
    setActiveIssueId(null);
    setAnchorPosition(null);
  }, [clearTimers]);

  // Try to close - only closes if neither highlight nor popover is hovered
  const tryClose = useCallback(() => {
    if (!isHighlightHoveredRef.current && !isPopoverHoveredRef.current) {
      setActiveIssueId(null);
      setAnchorPosition(null);
    }
  }, []);

  // Schedule close with persist delay
  const scheduleClose = useCallback(() => {
    // Clear any existing close timer
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    // Set new close timer
    closeTimerRef.current = setTimeout(() => {
      tryClose();
    }, hoverPersistDelay);
  }, [hoverPersistDelay, tryClose]);

  const handleMouseEnter = useCallback(
    (issueId: number, position: AnchorPosition) => {
      if (!enabled || Platform.OS !== 'web') return;

      isHighlightHoveredRef.current = true;

      // Clear any close timer (user came back)
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      // If already showing this issue, just update position
      if (activeIssueId === issueId) {
        setAnchorPosition(position);
        return;
      }

      // Clear any existing hover timer
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }

      // Set timer for hover delay
      hoverTimerRef.current = setTimeout(() => {
        activateIssue(issueId, position);
      }, hoverDelay);
    },
    [enabled, activeIssueId, hoverDelay, activateIssue]
  );

  const handleMouseLeave = useCallback(() => {
    if (Platform.OS !== 'web') return;

    isHighlightHoveredRef.current = false;

    // Clear hover timer (didn't stay long enough)
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    // Schedule close with persist delay
    scheduleClose();
  }, [scheduleClose]);

  const handlePress = useCallback(
    (issueId: number, position: AnchorPosition) => {
      if (!enabled) return;

      // Haptic feedback on native issue selection (DD-161)
      if (Platform.OS !== 'web') {
        Haptics.selectionAsync();
      }

      // Toggle on press (if same issue, deactivate; if different, activate)
      if (activeIssueId === issueId) {
        deactivateIssue();
      } else {
        activateIssue(issueId, position);
      }
    },
    [enabled, activeIssueId, activateIssue, deactivateIssue]
  );

  // Called by popover on mouse enter/leave
  const setPopoverHovered = useCallback(
    (hovered: boolean) => {
      isPopoverHoveredRef.current = hovered;

      if (hovered) {
        // Clear close timer when entering popover
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
      } else {
        // Schedule close when leaving popover
        scheduleClose();
      }
    },
    [scheduleClose]
  );

  return {
    activeIssueId,
    anchorPosition,
    activateIssue,
    deactivateIssue,
    handleMouseEnter,
    handleMouseLeave,
    handlePress,
    setPopoverHovered,
  };
}

export default useIssueInteraction;
